'use client'

import { Button } from '@/components/ui/button'
import { PageLayout } from '@/components/layout/page-layout'
import { ProductShell } from '@/components/layout/product-shell'
import {
  getLessons,
  getWordItems,
  getItemsForLesson,
  type PronunciationItem,
  type PronunciationLesson,
  type PronunciationScript
} from '@/content/pronunciation'
import { pronunciationPageCopy } from '@/content/pronunciation/page-copy'
import { getSoundSample } from '@/lib/pronunciation/sound-samples'
import { PronunciationChart } from './pronunciation-chart'
import {
  PronunciationLessonAccordion,
  type PronunciationSoundSection
} from './pronunciation-lesson-accordion'
import { PronunciationProgressSummary } from './pronunciation-progress-summary'
import { PronunciationQuiz } from './pronunciation-quiz'
import { useSettings } from '@/hooks/useSettings'
import { ScriptSwitch } from '@/components/script-switch'

const confusionBattles = [
  ['confusion-jqx', 'jqx', 'j / q / x'],
  ['confusion-zhchshr', 'zhchshr', 'zh / ch / sh / r'],
  ['confusion-zcs', 'zcs', 'z / c / s'],
  ['confusion-an-ang', 'nasals', 'an / ang'],
  ['confusion-en-eng', 'nasals', 'en / eng'],
  ['confusion-u-ü', 'jqx', 'u / ü'],
  ['confusion-tone-2-3', 'tones', '2nd / 3rd tone']
] as const

export function PronunciationPage({ script }: { script: PronunciationScript }) {
  const { settings, setScriptPriority } = useSettings()
  const copy = pronunciationPageCopy[script]
  const lessons = getLessons()

  const scriptPriority = settings.scriptPriority
  const toggleScript = () => {
    setScriptPriority(
      scriptPriority === 'simplified' ? 'traditional' : 'simplified'
    )
  }

  const scriptSwitch = (
    <ScriptSwitch script={scriptPriority} onToggle={toggleScript} />
  )

  return (
    <ProductShell variant="reference">
      <PageLayout
        variant="reference"
        title={copy.title}
        headerAction={scriptSwitch}
        headerActionCompact={scriptSwitch}
        headerActionCompactPlacement="inline"
      >
        <main className="mx-auto w-full max-w-6xl space-y-8 px-4 py-5 md:px-6 md:py-7">
          <PronunciationProgressSummary
            script={script}
            lessonCount={lessons.length}
          />
          <section id="chart" className="scroll-mt-24">
            <SectionTitle>{copy.chartTitle}</SectionTitle>
            <PronunciationChart script={script} />
          </section>
          <section id="lessons" className="scroll-mt-24">
            <SectionTitle>Lessons</SectionTitle>
            <div className="space-y-3">
              {lessons.map((lesson) => (
                <PronunciationLessonAccordion
                  key={lesson.id}
                  script={script}
                  lesson={lesson}
                  soundSections={buildSoundSections(lesson)}
                />
              ))}
            </div>
          </section>
          <ConfusionBattles script={script} />
          <Credits />
        </main>
      </PageLayout>
    </ProductShell>
  )
}

function ConfusionBattles({ script }: { script: PronunciationScript }) {
  return (
    <section>
      <SectionTitle>Confusion drills</SectionTitle>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {confusionBattles.map(([tag, lessonId, title]) => (
          <div
            key={tag}
            className="bg-background/80 flex items-center justify-between gap-3 rounded-2xl border px-4 py-3"
          >
            <span className="font-semibold">{title}</span>
            <PronunciationQuiz
              script={script}
              lessonId={lessonId}
              title={title}
              mode="confusion"
              confusionTag={tag}
              buttonLabel="Drill"
              buttonClassName="h-8"
            />
          </div>
        ))}
      </div>
    </section>
  )
}

function Credits() {
  return (
    <details className="bg-background/75 rounded-2xl border">
      <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold [&::-webkit-details-marker]:hidden">
        Audio credits
      </summary>
      <p className="text-muted-foreground border-t px-4 py-3 text-xs leading-5">
        Audio is from hugolpz/audio-cmn under CC BY-SA. Syllables are credited
        to Chen Wang; HSK word audio to Yue Tan / SWAC Recorder; curation,
        compression, and file naming to Hugo Lopez; recording software and
        technical support to Nicolas Vion.
      </p>
    </details>
  )
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="mb-3 text-2xl font-semibold tracking-tight">{children}</h2>
  )
}

function buildSoundSections(
  lesson: PronunciationLesson
): PronunciationSoundSection[] {
  const allWords = getWordItems()
  const soundTags = lesson.introducedSoundTags.filter(
    (tag) => tag.startsWith('initial-') || tag.startsWith('final-')
  )

  return soundTags.flatMap((tag) => {
    const sampleDetails = getSoundSample(tag)
    if (!sampleDetails) return []

    // Current lesson words matching the sound tag
    const currentLessonMatches = getItemsForLesson(lesson.id).words.filter(
      (w) => w.soundTags.includes(tag)
    )

    // Previous lesson words matching the sound tag
    const previousLessons = getLessons()
      .filter((l) => l.order < lesson.order)
      .sort((a, b) => a.order - b.order)
    const previousLessonMatches = previousLessons
      .flatMap((l) => getItemsForLesson(l.id).words)
      .filter((w) => w.soundTags.includes(tag))

    // Global fallback words matching the sound tag
    const globalMatches = allWords.filter((w) => w.soundTags.includes(tag))

    // Combine them in order, deduplicating by item id, and keeping max 2
    const seenIds = new Set<string>()
    const examples: PronunciationItem[] = []

    for (const word of [
      ...currentLessonMatches,
      ...previousLessonMatches,
      ...globalMatches
    ]) {
      if (!seenIds.has(word.id)) {
        seenIds.add(word.id)
        examples.push(word)
        if (examples.length >= 2) break
      }
    }

    return [
      {
        id: `${lesson.id}-${tag}`,
        soundTag: tag,
        pinyin: sampleDetails.pinyin,
        zhuyin: sampleDetails.zhuyin,
        audioPath: sampleDetails.audioPath,
        samplePinyin: sampleDetails.samplePinyin,
        sampleZhuyin: sampleDetails.sampleZhuyin,
        examples: examples
      }
    ]
  })
}
